import type { FileType } from '@unifyapps/defs/blocks/FileUpload/types';
import type { SlotPropsOverrides } from '@unifyapps/defs/blocks/TemplateComponent/types';
import type { EmitEventType } from '@unifyapps/defs/hooks/useBlockEvents';
import type { BlockStateUnionType, BlockType } from '@unifyapps/defs/types/block';
import type { JavaScriptFunctionState } from '@unifyapps/defs/types/javascriptFunction';
import type {
  JavaScriptVariable,
  JavaScriptVariableState,
} from '@unifyapps/defs/types/javascriptVariable';
import type { InterfacePageEntity } from '@unifyapps/defs/types/page';
import type { UseQueryResult } from '@unifyapps/network/react-query';
import type { RefObject } from 'react';
import type { RemountFunction } from '../../../components/Remount/Remount';
import type createObjectSetters from '../../../utils/setters/createObjectSetters';
import type { DependencyGraphStoreRefType } from '../../context/DependencyGraphProvider/types';
import type {
  DataSourceControllerActions,
  DataSourceControllerState,
} from '../../slices/DataSourceControllerSlice/types';
import type { InterfaceModes } from '../InterfaceStore';

export type {
  DataSourceMetaType,
  QueryRequestResult,
  QueryResult,
} from '../../slices/DataSourceControllerSlice/types';
export { GeoLocationStatus } from '../../stores/app/GlobalAppStateStore';
export type {
  DeviceDetailsType,
  LocationInStore,
  MobileUserExtraContext,
  UserContextInStore,
  UserExtraGeoLocationContext,
  WebUserExtraContext,
} from '../../stores/app/GlobalAppStateStore/types';

type QueryResult = UseQueryResult<Awaited<unknown>, unknown>;

type BlockSetterType = ReturnType<
  typeof createObjectSetters<GlobalStateStoreType, 'blocks', BlockStateUnionType>
>;

type PageVariableSetterType = ReturnType<
  typeof createObjectSetters<GlobalStateStoreType, 'pageVariables', JavaScriptVariableState>
>;

type PageFunctionSetterType = ReturnType<
  typeof createObjectSetters<GlobalStateStoreType, 'pageFunctions', JavaScriptFunctionState>
>;

type BlocksRefSetterType = ReturnType<
  typeof createObjectSetters<GlobalStateStoreType, 'blockRefs', RefObject<unknown>>
>;

type BlockLifecycleSetterType = ReturnType<
  typeof createObjectSetters<GlobalStateStoreType, 'blockLifecycles', BlockLifecycleStatus>
>;

type ContextualDialogForBlocksSetterType = ReturnType<
  typeof createObjectSetters<GlobalStateStoreType, 'contextualDialogForBlocks', string>
>;

type MediaUploadsSetterType = ReturnType<
  typeof createObjectSetters<GlobalStateStoreType, 'mediaUploads', FileType[]>
>;

type DraggableDataSetterType = ReturnType<
  typeof createObjectSetters<GlobalStateStoreType, 'draggableData', Record<string, unknown>>
>;

export type MappedMediaDetails = {
  url: string;
  caption?: string;
  mimeType?: string;
};
export type MappedMediaItems = {
  source?: string;
  mappedMediaDetails?: MappedMediaDetails;
};

export type BlockSelectionState = {
  selectedBlocks: BlockType[];
  hoveredBlock: BlockType | null;
};

export type GlobalStateStoreFlows = {
  importObjectRecords?: {
    onSuccess?: (...args: unknown[]) => unknown;
    onError?: (...args: unknown[]) => unknown;
    onClose?: (...args: unknown[]) => unknown;
    objectId?: string;
    objectInputs?: {
      key: string;
      value: string;
    }[];
  };
  shareEntityRecord?: {
    onSuccess?: (...args: unknown[]) => unknown;
    onError?: (...args: unknown[]) => unknown;
    onClose?: (...args: unknown[]) => unknown;
    entityId?: string;
    entityType?: string;
    variant?: 'MODAL' | 'POPOVER';

    // popover variant
    anchorBlockId?: string;
    title?: string;
    spaceId?: string;
    defaultRoleId?: string;
    allowedRoleIds?: string[];
  };
  controlMediaViewer?: {
    onSuccess?: (...args: unknown[]) => unknown;
    onError?: (...args: unknown[]) => unknown;
    onClose?: (...args: unknown[]) => unknown;
    url?: string;
    caption?: string;
    mimeType?: string;
    allowDownload?: boolean;
    mappedMediaItems?: MappedMediaItems;
    defaultIndex?: number;
  };
  blockSelector?: {
    onSuccess?: (selectedBlocks: BlockType[], hoveredBlock: BlockType | null) => void;
    onCancel?: () => void;
  };
};

export type SetPreBuiltFlowParams =
  | {
      type: 'importObjectRecords';
      data: GlobalStateStoreFlows['importObjectRecords'];
    }
  | {
      type: 'shareEntityRecord';
      data: GlobalStateStoreFlows['shareEntityRecord'];
    }
  | {
      type: 'controlMediaViewer';
      data: GlobalStateStoreFlows['controlMediaViewer'];
    }
  | {
      type: 'blockSelector';
      data: GlobalStateStoreFlows['blockSelector'];
    };

export enum BlockLifecycleStatus {
  Staging = 'staging',
  Unmounted = 'unmounted',
}

export type GlobalStateStoreInitialValue = Pick<
  GlobalStateStore,
  | 'pageInputs'
  | 'pageParams'
  | 'blocks'
  | 'pageVariables'
  | 'pageFunctions'
  | 'contextualDialogForBlocks'
  | 'onOutputChange'
> &
  Partial<
    Pick<
      GlobalStateStore,
      | 'blockLifecycles'
      | 'mediaUploads'
      | 'draggableData'
      | 'draggableBlockIds'
      | 'dataSources'
      | 'manualDataSources'
      | 'dataSourcesMeta'
      | 'manuallyTriggeredDataSources'
      | 'dataSourceIntraDependencies'
      | 'dataSourceDependencyFlow'
      | 'emitPageEvent'
      | 'blockSelectionActive'
    >
  >;

export type GlobalStateStoreRefType = {
  updateBlocks: ({
    added,
    deleted,
    changed,
  }: {
    added: BlockType[];
    deleted: BlockType[];
    changed: BlockType[];
  }) => void;
  updatePageVariables: ({
    added,
    deleted,
    changed,
  }: {
    added: JavaScriptVariable[];
    deleted: JavaScriptVariable[];
    changed: JavaScriptVariable[];
  }) => void;
  setPageVariables?: (variables: Record<string, JavaScriptVariableState>) => void;
  /** imperative read of a block's registered ref (state.blockRefs[blockId]). undefined when unregistered or at app scope (no blocks). */
  getBlockRef?: (blockId: string) => RefObject<unknown> | undefined;
};

export type BaseGlobalStateStoreProviderProps = {
  blocks?: InterfacePageEntity['properties']['blocks'];
  pageVariables?: InterfacePageEntity['properties']['pageVariables'];
  pageInputs?: Record<string, unknown>;
  pageParams?: Record<string, unknown>;
  // Component Overrides v2: TemplateComponent threads child-block content/appearance
  // override deltas through the inner page's GlobalStateStoreProvider so they
  // flow into block initial state (and react to runtime changes) instead of
  // riding on BlockRenderer addOns. Keyed by child blockId.
  componentOverrides?: SlotPropsOverrides;
  mode?: InterfaceModes;
  pageFunctions?: InterfacePageEntity['properties']['pageFunctions'];
  globalStateStoreRef?: RefObject<GlobalStateStoreRefType | undefined>;
  remount: () => void;
  getDependencyGraphActions: () => DependencyGraphStoreRefType;
  interfacePageId: string;
  interfacePageName: string;
};

export type WithRemountProps = Omit<BaseGlobalStateStoreProviderProps, 'remount'>;

export type GlobalStateStore = {
  blocks: Record<string, BlockStateUnionType | undefined>;

  pageVariables: Record<string, JavaScriptVariableState | undefined>;
  pageFunctions: Record<string, JavaScriptFunctionState | undefined>;

  pageInputs: Partial<Record<string, unknown>>;
  pageParams: Partial<Record<string, unknown>>;
  blockRefs: Record<string, RefObject<unknown> | undefined>;
  /**
   * when there is an attempt to render a block, i.e \<BlockRenderer id=\{blockId\} /\>, was called then the block is considered to be attempted to be mounted
   */
  blockLifecycles: Record<string, BlockLifecycleStatus | undefined>;
  flows: GlobalStateStoreFlows;
  //  a map of blockId and associated contextual dialog block id
  contextualDialogForBlocks: Record<string, string | undefined>;
  // a map of blockId and associated uploaded files
  mediaUploads: Record<string, FileType[] | undefined>;
  // a map of blockId and associated draggable data if that block is a draggable block
  draggableData: Record<string, Record<string, unknown> | undefined>;
  // list of block IDs that have draggable addon
  draggableBlockIds: string[];

  /**
   * when a module is mounted, the parent page passed this function to know when the module output changes
   */
  onOutputChange: (output: Record<string, unknown>) => void;
  /**
   * when an inline page instance is mounted, the parent passes this emitter so the
   * instance's blocks can bubble page events back to the host. lives in the
   * per-instance store (base store keeps it undefined) so action dispatch can
   * resolve the right emitter by instanceId without an extra context wrap.
   */
  emitPageEvent?: EmitEventType;
  /**
   * bound to useBlockEvents(interactions?.inboundEvents) on the module's own page.
   * seeded by useInboundEventEffects so that useControlBlockSelection can fire
   * standard events (e.g. OnComponentSelected) through the module's own interaction system.
   */
  dispatchPageEvent?: EmitEventType;
  blockSelection?: BlockSelectionState;
  blockSelectionActive?: boolean;
} & DataSourceControllerState;

export type GlobalStateStoreType = GlobalStateStore & {
  actions: {
    setBlockState: BlockSetterType['setSingle'];
    setBlocksState: BlockSetterType['setMultiple'];
    removeBlocksState: BlockSetterType['removeMultiple'];
    updateBlockState: BlockSetterType['updateSingle'];
    setPageVariableState: PageVariableSetterType['setSingle'];
    setPageVariablesState: PageVariableSetterType['setMultiple'];
    removePageVariableState: PageVariableSetterType['remove'];
    removePageFunctionState: PageFunctionSetterType['remove'];
    setPageInputsState: (pageInputs: Partial<Record<string, unknown>>) => void;
    setPageParamsState: (pageParams: Partial<Record<string, unknown>>) => void;
    setPageFunctionState: PageFunctionSetterType['setSingle'];
    setBlockLifecycle: BlockLifecycleSetterType['setSingle'];
    setContextualDialogForBlocks: ContextualDialogForBlocksSetterType['setMultiple'];
    setBlockRef: BlocksRefSetterType['setSingle'];
    resetBlockRefs: () => void;
    removeBlockRef: BlocksRefSetterType['remove'];
    setMediaUploads: MediaUploadsSetterType['setSingle'];
    setMultipleMediaUploads: MediaUploadsSetterType['setMultiple'];
    removeMediaUploads: MediaUploadsSetterType['remove'];
    setDraggableData: DraggableDataSetterType['setSingle'];
    setDraggableBlockIds: (blockIds: string[]) => void;
    setBlockSelection: (blockSelection: BlockSelectionState | undefined) => void;
    setPreBuiltFlow: (params: SetPreBuiltFlowParams) => void;
    removePreBuiltFlow: (key: keyof GlobalStateStoreFlows) => void;
    setDispatchPageEvent: (emitEvent: EmitEventType | undefined) => void;
    dataSource: DataSourceControllerActions;
    setIsInitialized: () => void;
    reset: () => void;
    remount: RemountFunction;
    setBlockSelectionActive: (active: boolean) => void;
  };
};
